import { IContentElement, IQuestionConfig } from 'src/app/ui-testrunner/models';
import {
  getMatrix,
  getScoringOptionKeys,
  IMatrixRowHeader,
  IScoreMatrix,
  scoreMatrixElementTypes,
} from '../../config-score-matrix/models';
import { getQuestionDeepElements } from './content-analysis';

/** @ticket https://bubo.vretta.com/vea/project-management/vretta-project-notes/eqao/authoring/-/work_items/1938 */

export const ITEM_MATRIX_AUDIT_SLUG = 'ITEM_MATRIX_AUDIT';
export const ITEM_MATRIX_AUTOFIX_SLUG = 'ITEM_MATRIX_AUDIT_IS_AUTOFIXING';
export const ITEM_MATRIX_AUTOFIX_CHECK_IDS = ['UNCONFIRMED', 'OUTDATED'];

/**
 * Element-level scoring settings copied onto every matrix row in September 2026 (00938737e2, cc1debd691).
 * Rows confirmed before then do not carry them, which alone marks those matrices as outdated.
 */
export const ROW_SCORING_SETTING_KEYS = [
  'scoreWeight',
  'enableProportionalScoring',
  'scoreMode',
  'maxCheckedPerRow',
  'maxCheckedPerCol',
  'isMaxDenoScoring',
  'isMustHaveOnePerRow',
  'isMustHaveOnePerCol',
  'isMultiSelect',
  'maxOptions',
  'isEnablePartialweights',
  'isOptional',
];

export enum EMatrixReconfirmClass {
  CURRENT = 'CURRENT',
  RECONFIRM_SAFE = 'RECONFIRM_SAFE',
  UNCONFIRMED = 'UNCONFIRMED',
  CHANGED = 'CHANGED',
}

export interface IMatrixClassification {
  cls: EMatrixReconfirmClass;
  reasons: string[];
}

export interface IMatrixReconfirmEntry extends IMatrixClassification {
  questionId: number;
  label: string;
  lang: string;
  entryId: number | string;
  elementType: string;
}

export interface IMatrixReconfirmPlan {
  entries: IMatrixReconfirmEntry[];
  counts: Record<EMatrixReconfirmClass, number>;
  /** item labels per class, each label once */
  labels: Record<EMatrixReconfirmClass, string[]>;
}

export interface IMatrixReconfirmResult {
  reconfirmed: number;
  confirmed: number;
  skipped: number;
}

const LANGS = ['en', 'fr'];

const isDefined = (v: any) => v !== undefined;

/** JSON form with sorted keys and undefined values dropped, i.e. what survives a save/reload */
const canonical = (value: any): string => JSON.stringify(normalize(value));
const normalize = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(v => (v === undefined ? null : normalize(v)));
  }
  if (value && typeof value === 'object') {
    const out = {};
    Object.keys(value).sort().forEach(k => {
      if (isDefined(value[k])) {
        out[k] = normalize(value[k]);
      }
    });
    return out;
  }
  return value;
};

const definedKeys = (row: any): string[] => Object.keys(row ?? {}).filter(k => isDefined(row[k]));

const isScoringOptionEqual = (prev: any, curr: any, elementType: string): boolean => {
  return getScoringOptionKeys(elementType).every(k => {
    const a = prev?.[k];
    const b = curr?.[k];
    return a === b || (!a && !b);
  });
};

/** Differences between stored and regenerated rows; `hasSettingDrift` when only the September keys are missing */
const diffRows = (stored: IMatrixRowHeader[], generated: IMatrixRowHeader[], elementType: string) => {
  const reasons: string[] = [];
  let hasSettingDrift = false;
  if ((stored?.length ?? 0) !== (generated?.length ?? 0)) {
    reasons.push(`row count ${stored?.length ?? 0} -> ${generated?.length ?? 0}`);
    return { reasons, hasSettingDrift };
  }
  generated.forEach((gen, idx) => {
    const prev = stored[idx] ?? <IMatrixRowHeader>{};
    const rowNum = idx + 1;
    definedKeys(gen).forEach(key => {
      if (key === 'option') {
        if (!isScoringOptionEqual(prev.option, gen.option, elementType)) {
          reasons.push(`row ${rowNum} option id`);
        }
      } else if (!isDefined(prev[key])) {
        if (ROW_SCORING_SETTING_KEYS.includes(key)) {
          hasSettingDrift = true;
        } else {
          reasons.push(`row ${rowNum} ${key} added`);
        }
      } else if (canonical(prev[key]) !== canonical(gen[key])) {
        reasons.push(`row ${rowNum} ${key}`);
      }
    });
    definedKeys(prev).forEach(key => {
      if (key !== 'option' && !isDefined(gen[key])) {
        reasons.push(`row ${rowNum} ${key} removed`);
      }
    });
  });
  return { reasons, hasSettingDrift };
};

/** Compare an element's stored score matrix against the one its current config would generate */
export const classifyScoreMatrixElement = (element: IContentElement): IMatrixClassification => {
  const stored: IScoreMatrix = element.scoreMatrix;
  if (!stored || !stored.isConfirmed) {
    return { cls: EMatrixReconfirmClass.UNCONFIRMED, reasons: [] };
  }
  let generated;
  try {
    generated = getMatrix(element);
  } catch (e) {
    return { cls: EMatrixReconfirmClass.CHANGED, reasons: ['matrix could not be rebuilt'] };
  }
  const reasons: string[] = [];
  if (canonical(stored.columns ?? []) !== canonical(generated.columns)) {
    reasons.push('columns');
  }
  if (canonical(stored.values ?? []) !== canonical(generated.values)) {
    reasons.push('values');
  }
  const rowDiff = diffRows(stored.rows ?? [], generated.rows, element.elementType);
  reasons.push(...rowDiff.reasons);
  if (reasons.length) {
    return { cls: EMatrixReconfirmClass.CHANGED, reasons };
  }
  if (rowDiff.hasSettingDrift || !stored.isUpdated) {
    return { cls: EMatrixReconfirmClass.RECONFIRM_SAFE, reasons: [] };
  }
  return { cls: EMatrixReconfirmClass.CURRENT, reasons: [] };
};

export const getScoreMatrixElements = (question: IQuestionConfig, lang: string): IContentElement[] => {
  if (lang !== 'en' && !question?.langLink) {
    return [];
  }
  return getQuestionDeepElements(question, lang)
    .filter(el => scoreMatrixElementTypes.includes(<any>el?.elementType));
};

const emptyByClass = <T>(make: () => T): Record<EMatrixReconfirmClass, T> => ({
  [EMatrixReconfirmClass.CURRENT]: make(),
  [EMatrixReconfirmClass.RECONFIRM_SAFE]: make(),
  [EMatrixReconfirmClass.UNCONFIRMED]: make(),
  [EMatrixReconfirmClass.CHANGED]: make(),
});

/** Classify every score matrix (both languages) on the given items without changing anything */
export const planScoreMatrixReconfirm = (questions: IQuestionConfig[]): IMatrixReconfirmPlan => {
  const entries: IMatrixReconfirmEntry[] = [];
  const counts = emptyByClass(() => 0);
  const labels = emptyByClass<string[]>(() => []);
  const seen = new Set<number>();
  (questions ?? []).forEach(question => {
    if (!question || seen.has(question.id)) {
      return;
    }
    seen.add(question.id);
    LANGS.forEach(lang => {
      getScoreMatrixElements(question, lang).forEach(element => {
        const { cls, reasons } = classifyScoreMatrixElement(element);
        entries.push({
          questionId: question.id,
          label: question.label,
          lang,
          entryId: element.entryId,
          elementType: element.elementType,
          cls,
          reasons,
        });
        counts[cls]++;
        if (!labels[cls].includes(question.label)) {
          labels[cls].push(question.label);
        }
      });
    });
  });
  return { entries, counts, labels };
};

const writeGeneratedMatrix = (element: IContentElement) => {
  const { rows, columns, values } = JSON.parse(JSON.stringify(getMatrix(element)));
  element.scoreMatrix = {
    ...(element.scoreMatrix ?? {}),
    rows,
    columns,
    values,
    isConfirmed: true,
    isUpdated: true,
  };
};

/**
 * Re-confirm matrices whose answer key is unchanged (rows refreshed, confirmation kept).
 * Never-confirmed matrices are confirmed from the current config only with `includeUnconfirmed`.
 * Matrices whose scoring changed are left for the author to review and confirm.
 */
export const applyScoreMatrixReconfirm = (question: IQuestionConfig, opts: { includeUnconfirmed: boolean }): IMatrixReconfirmResult => {
  const result: IMatrixReconfirmResult = { reconfirmed: 0, confirmed: 0, skipped: 0 };
  LANGS.forEach(lang => {
    getScoreMatrixElements(question, lang).forEach(element => {
      const { cls } = classifyScoreMatrixElement(element);
      if (cls === EMatrixReconfirmClass.RECONFIRM_SAFE) {
        writeGeneratedMatrix(element);
        result.reconfirmed++;
      } else if (cls === EMatrixReconfirmClass.UNCONFIRMED && opts.includeUnconfirmed) {
        writeGeneratedMatrix(element);
        result.confirmed++;
      } else if (cls !== EMatrixReconfirmClass.CURRENT) {
        result.skipped++;
      }
    });
  });
  return result;
};

/** The DB audit catalog has no auto-fix for this audit; attach the built-in one unless the catalog defines its own */
export const withScoreMatrixAutoFix = <T extends { slug: string, autoFixes?: any, isCompleteFix?: boolean, checks?: any[] }>(audits: T[]): T[] => {
  const audit = audits?.find(a => a.slug === ITEM_MATRIX_AUDIT_SLUG);
  if (!audit || audit.autoFixes) {
    return audits;
  }
  audit.autoFixes = {
    slug: ITEM_MATRIX_AUTOFIX_SLUG,
    caption: { en: 'Re-confirm unchanged matrices', fr: 'Reconfirmer les matrices inchangées' },
  };
  audit.isCompleteFix = false;
  audit.checks?.forEach(check => {
    if (ITEM_MATRIX_AUTOFIX_CHECK_IDS.includes(check.id)) {
      check.autoFix = true;
    }
  });
  return audits;
};

/**
 * Audits run in French hold French-only copies (French content as `content`, no `langLink`).
 * Resolve each flagged item to its full item bank question so both languages are planned and tagged correctly.
 */
export const resolveFlaggedQuestions = (
  flagged: IQuestionConfig[],
  getFullQuestion: (id: number) => IQuestionConfig | undefined,
): IQuestionConfig[] => {
  const seen = new Set<number>();
  const resolved: IQuestionConfig[] = [];
  (flagged ?? []).forEach(question => {
    if (!question || seen.has(question.id)) {
      return;
    }
    seen.add(question.id);
    resolved.push(getFullQuestion(question.id) ?? question);
  });
  return resolved;
};

/**
 * Items the auto-fix opens: any item with at least one fixable matrix in either language, then items
 * with flagged expected answers (re-validated only). Each item once, in that order.
 */
export const selectScoreMatrixFixQuestions = (
  questions: IQuestionConfig[],
  plan: IMatrixReconfirmPlan,
  opts: { includeUnconfirmed: boolean, flaggedAnswerQuestions?: IQuestionConfig[] },
): IQuestionConfig[] => {
  const fixClasses = opts.includeUnconfirmed
    ? [EMatrixReconfirmClass.RECONFIRM_SAFE, EMatrixReconfirmClass.UNCONFIRMED]
    : [EMatrixReconfirmClass.RECONFIRM_SAFE];
  const fixableIds = new Set(plan.entries.filter(e => fixClasses.includes(e.cls)).map(e => e.questionId));
  const selected: IQuestionConfig[] = [];
  const seen = new Set<number>();
  const add = (question: IQuestionConfig) => {
    if (question && !seen.has(question.id)) {
      seen.add(question.id);
      selected.push(question);
    }
  };
  (questions ?? []).filter(q => fixableIds.has(q?.id)).forEach(add);
  (opts.flaggedAnswerQuestions ?? []).forEach(add);
  return selected;
};
